import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import { plFilterValues } from "./Fleetedgekpi"
import { FilterContext } from "../../context/Filter";

const FleetEdgeFilter = () => {
  const {
    arn: parArn,
    childArn,
    fleetEdgeDropDownPayload,
    setFleetEdgeDropDownPayload,
    fleetedgePayload,
    setFleetEdgePayload,
  } = useContext(FilterContext);

  const [plOptions, setPlOptions] = useState([]);
  const [selectedPl, setSelectedPl] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingDropdown, setUpdatingDropdown] = useState(false);

  const syncInProgress = useRef(false);
  const hasInitialized = useRef(false);

  const shouldEnableQuery = Boolean(parArn || childArn);

  /** Sync from context payload */
  useEffect(() => {
    if (fleetEdgeDropDownPayload && !syncInProgress.current) {
      syncInProgress.current = true;
      setSelectedPl(fleetEdgeDropDownPayload.pl?.[0] || "");
      setTimeout(() => {
        syncInProgress.current = false;
      }, 100);
    }
  }, [fleetEdgeDropDownPayload]);

  /** Generate payload for API */
  /** Generate payload for API */
  const generatePayload = useCallback(() => {
    const payload = {
      ...(childArn ? { childArn } : { parArn }),
    };
    if (selectedPl) payload.filterValue = [selectedPl];
    return payload;
  }, [parArn, childArn, selectedPl]);

  /** Fetch PL dropdown */
  const fetchPlOptions = useCallback(
    async (changedValue = null) => {
      if (!shouldEnableQuery) return;
      setUpdatingDropdown(true);
      try {
        const payload = {
          ...(childArn ? { childArn } : { parArn }),
        };
        if (changedValue) payload.filterValue = [changedValue];
        else if (selectedPl) payload.filterValue = [selectedPl];

        const plResp = await plFilterValues(parArn, childArn, payload);
        setPlOptions(plResp || []);

        if (selectedPl && !plResp?.some((item) => item.key === selectedPl)) {
          setSelectedPl("");
        }
      } catch (error) {
        console.error("Error fetching PL options:", error);
      } finally {
        setUpdatingDropdown(false);
      }
    },
    [parArn, childArn, selectedPl, shouldEnableQuery]
  );

  /** Handle PL change */
  const handlePlChange = useCallback(
    (value) => {
      setSelectedPl(value);
      if (value) {
        fetchPlOptions(value);
      }
    },
    [fetchPlOptions]
  );

  /** Submit filter selection */
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (syncInProgress.current || updatingDropdown) return;

      syncInProgress.current = true;
      const payload = generatePayload();

      setFleetEdgeDropDownPayload(payload);
      setFleetEdgePayload(payload);

      setTimeout(() => {
        syncInProgress.current = false;
      }, 500);
    },
    [
      generatePayload,
      updatingDropdown,
      setFleetEdgeDropDownPayload,
      setFleetEdgePayload,
    ]
  );

  /** Reset filter */
  const handleReset = useCallback(() => {
    if (syncInProgress.current || updatingDropdown) return;
    syncInProgress.current = true;

    const resetPayload = {
      ...(childArn ? { childArn } : { parArn }),
      search_type: [],
      dropDownValue: [],
    };

    setSelectedPl("");
    setFleetEdgeDropDownPayload(resetPayload);
    setFleetEdgePayload(resetPayload);

    fetchPlOptions(null).finally(() => {
      setTimeout(() => {
        syncInProgress.current = false;
      }, 500);
    });
  }, [
    parArn,
    childArn,
    updatingDropdown,
    setFleetEdgeDropDownPayload,
    setFleetEdgePayload,
    fetchPlOptions,
  ]);

  /** Initialize on first render */
  useEffect(() => {
    if (shouldEnableQuery && !hasInitialized.current) {
      hasInitialized.current = true;
      setLoading(true);
      fetchPlOptions(null).finally(() => {
        setLoading(false);
      });
    }
  }, [shouldEnableQuery, fetchPlOptions]);

  if (loading) {
    return (
      <div className="flex justify-center items-center">
        <div className="text-xl font-semibold">Loading Filters...</div>
      </div>
    );
  }
  // console.log("selectedPl", selectedPl)
  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-full mx-auto p-4 bg-white rounded-md shadow-md flex flex-col gap-2"
    >
      <Dropdown
        label="PL"
        value={selectedPl}
        onChange={handlePlChange}
        options={plOptions?.result?.buckets}
        loading={updatingDropdown}
      />

      <div className="flex gap-2 justify-center">
        <button
          type="submit"
          disabled={updatingDropdown}
          className={`px-3 py-1 rounded-md text-white ${updatingDropdown
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-800"
            }`}
        >
          Submit
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={updatingDropdown}
          className="px-3 py-1 rounded-md bg-gray-500 text-white hover:bg-gray-700 disabled:cursor-not-allowed"
        >
          Reset
        </button>
      </div>
    </form>
  );
};

/** Simple reusable dropdown */
const Dropdown = React.memo(
  ({ label, value, onChange, options, loading = false }) => (
    <div className="flex gap-2 items-center text-sm">
      <label>{label}:</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className={`border border-gray-400 rounded-md py-1 px-2 ${loading ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
      >
        <option value="">
          {loading ? `Loading ${label}...` : `Select ${label}`}
        </option>
        {options?.map((item) => (
          <option key={item.key || item.value} value={item.key || item.value}>
            {item.key || item.label}
          </option>
        ))}
      </select>
    </div>
  )
);

Dropdown.displayName = "Dropdown";

export default FleetEdgeFilter;
